import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo correu mal. Tenta novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLoginPage() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!loading && user && user.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Sessão iniciada");
      navigate("/admin");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen grid place-items-center px-5 bg-grain"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-grid place-items-center h-14 w-14 bg-[#00F0FF] text-black mb-4">
            <ShieldCheck className="h-7 w-7" strokeWidth={2} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">
            Painel de Administração
          </div>
          <h1 className="font-display text-4xl uppercase tracking-wider mt-2">
            Iniciar Sessão
          </h1>
        </div>

        <form
          onSubmit={onSubmit}
          className="border border-white/10 bg-[#141414] p-7 space-y-5"
        >
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-white/60">
              Email
            </label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="mt-1 block w-full bg-black border border-white/10 px-4 py-3 text-white outline-none focus:border-[#00F0FF] transition-colors"
              placeholder="admin@estel.pt"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-white/60">
              Palavra-passe
            </label>
            <div className="relative">
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-1 block w-full bg-black border border-white/10 px-4 py-3 pr-10 text-white outline-none focus:border-[#00F0FF] transition-colors"
              />
              <Lock className="absolute right-3 top-1/2 -translate-y-1/3 h-4 w-4 text-white/30" />
            </div>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2 text-sm text-[#FF3B30]"
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={submitting}
            className="w-full bg-[#00F0FF] text-black px-5 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-white disabled:opacity-50 transition-colors"
          >
            {submitting ? "A entrar..." : "Entrar"}
          </button>
        </form>

        <button
          onClick={() => navigate("/")}
          data-testid="back-home"
          className="block mx-auto mt-6 text-xs uppercase tracking-[0.25em] text-white/40 hover:text-[#00F0FF]"
        >
          ← Voltar ao site
        </button>
      </div>
    </div>
  );
}
